import React from 'react'
import { Card } from 'primereact/card'
import { Message } from 'primereact/message'
import { Badge } from 'primereact/badge'
import { Divider } from 'primereact/divider'
import { Panel } from 'primereact/panel'
import { useOrderData } from '../../../hooks/useOrderData'
import { useOrderSelection } from '../../../hooks/useOrderSelection'
import { useLineItems } from '../../../hooks/useLineItems'
import { useLineItemSelection } from '../../../hooks/useLineItemSelection'

interface OrderAnalysis {
  storeDeclinedOnly: number
  adminRejectedOnly: number
  mixedOrders: number
  totalOrders: number
}

interface ReassignmentConfirmationViewProps {
  orderData: ReturnType<typeof useOrderData>
  orderSelection: ReturnType<typeof useOrderSelection>
  lineItemsHook: ReturnType<typeof useLineItems>
  lineItemSelection: ReturnType<typeof useLineItemSelection>
  orderAnalysis: OrderAnalysis | null
}

export const ReassignmentConfirmationView: React.FC<ReassignmentConfirmationViewProps> = ({
  orderData,
  orderSelection,
  lineItemsHook,
  lineItemSelection,
  orderAnalysis
}) => {
  if (!orderAnalysis) return null

  const selectedOrders = orderSelection.getSelectedOrdersData()
  const totalRejectedItems = selectedOrders.reduce((sum, order) => sum + (order.declined_items || 0), 0)
  const totalOrderValue = selectedOrders.reduce((sum, order) => sum + order.order_subtotal, 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', padding: '1rem 0' }}>
      {/* Warning Message */}
      <Message
        severity="warn"
        content={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.25rem' }}></i>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Confirm Bulk Reassignment</div>
              <div style={{ fontSize: '0.875rem' }}>
                This action will automatically reassign all rejected items to stores with the highest inventory levels. 
                This process cannot be undone.
              </div>
            </div>
          </div>
        }
        style={{ marginBottom: '0.5rem' }}
      />

      {/* Summary Card */}
      <Card title="Reassignment Summary" className="w-full" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ flex: '1 1 240px', minWidth: '240px' }}>
            <Panel className="h-full" style={{ 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', 
              border: '2px solid #2196f3',
              boxShadow: '0 4px 8px rgba(33, 150, 243, 0.15)',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  backgroundColor: '#1976d2', 
                  borderRadius: '50%', 
                  padding: '0.75rem',
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)'
                }}>
                  <i className="pi pi-shopping-cart" style={{ fontSize: '1.5rem', color: 'white' }}></i>
                </div>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '1.25rem', color: '#0d47a1' }}>{orderAnalysis.totalOrders}</div>
                  <div style={{ fontSize: '0.875rem', color: '#1565c0', fontWeight: '500' }}>Orders to Process</div>
                </div>
              </div>
            </Panel>
          </div>
          <div style={{ flex: '1 1 240px', minWidth: '240px' }}>
            <Panel className="h-full" style={{ 
              background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)', 
              border: '2px solid #ff9800',
              boxShadow: '0 4px 8px rgba(255, 152, 0, 0.15)',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  backgroundColor: '#f57c00', 
                  borderRadius: '50%', 
                  padding: '0.75rem',
                  boxShadow: '0 2px 4px rgba(245, 124, 0, 0.3)'
                }}>
                  <i className="pi pi-sync" style={{ fontSize: '1.5rem', color: 'white' }}></i>
                </div>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '1.25rem', color: '#e65100' }}>{totalRejectedItems}</div>
                  <div style={{ fontSize: '0.875rem', color: '#f57c00', fontWeight: '500' }}>Items to Reassign</div>
                </div>
              </div>
            </Panel>
          </div>
          <div style={{ flex: '1 1 240px', minWidth: '240px' }}>
            <Panel className="h-full" style={{ 
              background: 'linear-gradient(135deg, #e8f5e8 0%, #a5d6a7 100%)', 
              border: '2px solid #4caf50',
              boxShadow: '0 4px 8px rgba(76, 175, 80, 0.15)',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  backgroundColor: '#388e3c', 
                  borderRadius: '50%', 
                  padding: '0.75rem',
                  boxShadow: '0 2px 4px rgba(56, 142, 60, 0.3)'
                }}>
                  <i className="pi pi-dollar" style={{ fontSize: '1.5rem', color: 'white' }}></i>
                </div>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '1.25rem', color: '#1b5e20' }}>${totalOrderValue.toFixed(2)}</div>
                  <div style={{ fontSize: '0.875rem', color: '#2e7d32', fontWeight: '500' }}>Total Order Value</div>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      </Card>

      <Divider style={{ margin: '1rem 0' }} />

      {/* How It Works */}
      <Card title="How Automatic Reassignment Works" className="w-full" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Panel style={{ 
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            border: '2px solid #2196f3',
            boxShadow: '0 3px 6px rgba(33, 150, 243, 0.1)',
            borderLeft: '5px solid #1976d2'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <div style={{ 
                backgroundColor: '#1976d2', 
                borderRadius: '50%', 
                padding: '0.5rem',
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                marginTop: '0.125rem'
              }}>
                <i className="pi pi-info-circle" style={{ fontSize: '1.25rem', color: 'white' }}></i>
              </div>
              <div>
                <div style={{ fontWeight: '700', color: '#0d47a1', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Smart Store Selection</div>
                <p style={{ fontSize: '0.875rem', color: '#1565c0', margin: '0', lineHeight: '1.6' }}>
                  Our system will automatically assign rejected items to stores with the highest inventory levels 
                  and best fulfillment capacity for each specific product.
                </p>
              </div>
            </div>
          </Panel>

          <Panel style={{ 
            background: 'linear-gradient(135deg, #e8f5e8 0%, #a5d6a7 100%)',
            border: '2px solid #4caf50',
            boxShadow: '0 3px 6px rgba(76, 175, 80, 0.1)',
            borderLeft: '5px solid #388e3c'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <div style={{ 
                backgroundColor: '#388e3c', 
                borderRadius: '50%', 
                padding: '0.5rem',
                boxShadow: '0 2px 4px rgba(56, 142, 60, 0.3)',
                marginTop: '0.125rem'
              }}>
                <i className="pi pi-check-circle" style={{ fontSize: '1.25rem', color: 'white' }}></i>
              </div>
              <div>
                <div style={{ fontWeight: '700', color: '#1b5e20', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Optimized Distribution</div>
                <p style={{ fontSize: '0.875rem', color: '#2e7d32', margin: '0', lineHeight: '1.6' }}>
                  Items will be distributed across multiple stores when needed to ensure faster fulfillment 
                  and reduce the risk of future rejections.
                </p>
              </div>
            </div>
          </Panel>

        </div>
      </Card>

      {/* Final Confirmation */}
      <Message
        severity="info"
        content={
          <div style={{ display: 'flex', alignItems: 'left', gap: '0.75rem' }}>
            <i className="pi pi-question-circle" style={{ fontSize: '1.25rem' }}></i>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Ready to Proceed?</div>
              <div style={{ fontSize: '0.875rem' }}>
                Click <strong>"Execute Reassignment"</strong> to begin the automatic assignment process.
              </div>
            </div>
          </div>
        }
      />
    </div>
  )
} 